/**
 * Ширина левой панели настроек персонала.
 * Повторяет поведение разделителя карточки сущности, без импорта из модуля чата.
 */
import { onBeforeUnmount, ref, watch } from 'vue'

export interface IUseStaffSettingsPaneParams {
  initial: number
  min: number
  max: number | (() => number)
  storageKey?: string
}

const LS_PREFIX = 'materio.pane-width.'

function resolveMax(max: number | (() => number)): number {
  return typeof max === 'function' ? max() : max
}

export function useStaffSettingsPane(params: IUseStaffSettingsPaneParams) {
  const clamp = (value: number) => {
    const maxValue = resolveMax(params.max)
    return Math.min(Math.max(value, params.min), maxValue)
  }

  const stored = params.storageKey
    ? Number(localStorage.getItem(LS_PREFIX + params.storageKey))
    : NaN
  const width = ref(clamp(Number.isFinite(stored) ? stored : params.initial))
  const isResizing = ref(false)

  watch(width, value => {
    if (!params.storageKey || value == null) return
    localStorage.setItem(LS_PREFIX + params.storageKey, String(Math.round(value)))
  })

  let startX = 0
  let startWidth = 0

  const onMouseMove = (event: MouseEvent) => {
    const delta = event.clientX - startX
    width.value = clamp(startWidth + delta)
  }

  const onMouseUp = () => {
    isResizing.value = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  const startResize = (event: MouseEvent) => {
    event.preventDefault()
    startX = event.clientX
    startWidth = width.value
    isResizing.value = true
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  })

  return { width, startResize, isResizing }
}
